import React, { useEffect, useMemo, useState } from 'react';
import { useRouteBlocker } from '@/hooks/useRouteBlocker.ts';
import { Form, FormikProvider, useFormik } from 'formik';
import { toast } from 'react-toastify';
import styles from './Settings.module.scss';
import Card from '@/components/ui/Card/Card.tsx';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import FormikCheckboxToggle from '@/components/Formik/FormikCheckboxToggle.tsx';
import FormikSelect from '@/components/Formik/FormikSelect.tsx';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import { getEntitiesFilters } from '@/api/climatePro.ts';
import UnsavedChangesToast from '@/pages/ClimateProDashboard/UnsavedChangesToast/UnsavedChangesToast.tsx';
import CloseIcon from '@/assets/Icons/Close.svg?react';
import { getDataComplianceSettings, setDataComplianceSettings } from '@/api/dataCompliance.ts';
import UpdateDataComplianceSettingsRequest = App.Data.Request.UpdateDataComplianceSettingsRequest;
import DataComplianceExclusionData = App.Data.DataComplianceExclusionData;
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import breakpoints from '@/constants/breakpoints.ts';
import SubmenuItems from '@/components/SubmenuItems/SubmenuItems.tsx';
import { submenuLinks } from '@/pages/ClimateProDashboard/DataCompliance/PastTrips/PastTrips.tsx';
import { useWindowSize } from '@react-hook/window-size';
import InfoStatus from '@/components/ui/InfoStatus/InfoStatus.tsx';
import ArrowDownIcon from '@/assets/Icons/Arrow-Down.svg?react';
import classNames from 'classnames';
import { trackEvent, trackPostHogEvent } from '@/utils/productAnalyticsUtil.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';

type ExclusionValue = Pick<DataComplianceExclusionData, 'type' | 'id'> &
	Partial<Pick<DataComplianceExclusionData, 'exclude'>>;

const serializeExclusions = (exclusions: ExclusionValue[] = []) =>
	exclusions
		.map(exclusion => `${exclusion.type}:${exclusion.id}`)
		.sort()
		.join('|');

const Settings: React.FC = () => {
	const [width] = useWindowSize();
	const [isExclusionsOpen, setIsExclusionsOpen] = useState(false);

	useEffect(() => {
		trackPostHogEvent('data_gap_settings_viewed', { category: 'data-gap' });
	}, []);

	const {
		data: entitiesData,
		isError: isEntitiesError,
		isLoading: isEntitiesLoading,
		isSuccess: isEntitiesSuccess,
	} = useQuery({
		queryKey: [queryKeys.entitiesFilter],
		queryFn: getEntitiesFilters,
	});

	const {
		data: settingsData,
		refetch,
		isError,
		isLoading,
		isSuccess,
	} = useQuery({
		queryKey: [queryKeys.dataComplianceSettings],
		queryFn: getDataComplianceSettings,
	});

	const initialActiveExclusions = useMemo(
		() => (settingsData?.exclusions ?? []).filter(exclusion => exclusion.exclude),
		[settingsData?.exclusions],
	);

	const initialValues = useMemo(() => {
		return {
			enabled: settingsData?.enabled ?? true,
			exclusions: initialActiveExclusions,
			legal_entityExclusions: undefined,
			officeExclusions: undefined,
			departmentExclusions: undefined,
			userExclusions: undefined,
		};
	}, [initialActiveExclusions, settingsData?.enabled]);

	const formik = useFormik({
		initialValues,
		enableReinitialize: true,
		onSubmit: async values => {
			const current = values.exclusions.map(e => ({ type: e.type, id: e.id }));
			const initialActive = initialActiveExclusions.map(e => ({ type: e.type, id: e.id }));

			const added = current.filter(c => !initialActive.some(i => i.type === c.type && i.id === c.id));
			const removed = initialActive.filter(i => !current.some(c => c.type === i.type && c.id === i.id));

			const payload: UpdateDataComplianceSettingsRequest = {
				enabled: !!values.enabled,
				exclusions: [
					...added.map(e => ({ ...e, exclude: true })),
					...removed.map(e => ({ ...e, exclude: false })),
				],
			};

			try {
				if (
					(payload.exclusions && payload.exclusions.length > 0) ||
					payload.enabled !== (settingsData?.enabled ?? true)
				) {
					await setDataComplianceSettings(payload);
					trackEvent(productAnalyticsEvents.climatePro.dataGap.settings.submit);
					const updated = await refetch();
					if (updated.data) {
						formik.resetForm({
							values: {
								enabled: updated.data.enabled,
								exclusions: (updated.data.exclusions ?? []).filter(e => e.exclude),
								legal_entityExclusions: undefined,
								officeExclusions: undefined,
								departmentExclusions: undefined,
								userExclusions: undefined,
							},
						});
					}
					if (payload.exclusions?.length) {
						trackPostHogEvent('data_gap_exclusions_submitted', {
							category: 'data-gap',
							added_count: added.length,
							removed_count: removed.length,
						});
					}
					toast.success('Reminder settings saved.');
				} else {
					toast.warning('No changes to save.');
				}
			} catch {
				toast.error('We couldn’t save your reminder settings. Try again.');
			}
		},
	});

	const hasUnsavedChanges = useMemo(() => {
		if (isLoading || !settingsData) return false;

		return (
			settingsData.enabled !== formik.values.enabled ||
			serializeExclusions(formik.values.exclusions) !== serializeExclusions(initialActiveExclusions)
		);
	}, [formik.values.enabled, formik.values.exclusions, initialActiveExclusions, isLoading, settingsData]);

	useRouteBlocker(hasUnsavedChanges);

	const addExclusion = (type: string, value?: string) => {
		if (!value) return;

		const label =
			entitiesData?.entities?.find(ent => ent.value === type)?.items.find(ent => ent.value === Number(value))
				?.label ?? '';

		if (!formik.values.exclusions.some(e => e.type === type && e.id === Number(value))) {
			formik.setFieldValue('exclusions', [
				...formik.values.exclusions,
				{
					type,
					id: Number(value),
					label,
					exclude: true,
				},
			]);
		}
		formik.setFieldValue(`${type}Exclusions`, undefined);
	};

	const removeExclusion = (type: string, id: number) => {
		formik.setFieldValue(
			'exclusions',
			formik.values.exclusions.filter(e => !(e.type === type && e.id === id)),
		);
	};

	useEffect(() => {
		if (formik.values.legal_entityExclusions) {
			addExclusion('legal_entity', formik.values.legal_entityExclusions);
		}
	}, [formik.values.legal_entityExclusions]);

	useEffect(() => {
		if (formik.values.officeExclusions) {
			addExclusion('office', formik.values.officeExclusions);
		}
	}, [formik.values.officeExclusions]);

	useEffect(() => {
		if (formik.values.departmentExclusions) {
			addExclusion('department', formik.values.departmentExclusions);
		}
	}, [formik.values.departmentExclusions]);

	useEffect(() => {
		if (formik.values.userExclusions) {
			addExclusion('user', formik.values.userExclusions);
		}
	}, [formik.values.userExclusions]);

	const renderSelectSection = (type: string, label: string) => {
		const entityItems = entitiesData?.entities.find(e => e.value === type)?.items || [];
		if (entityItems.length <= 1) return null;

		return (
			<div className={styles.toggleSection}>
				<FlexBlock justifyContent="space-between" alignItems="center" columnGap="20px">
					<p>{label}</p>
					<FormikSelect
						searchable
						name={`${type}Exclusions`}
						options={entityItems}
						onValueChange={value => {
							if (value !== undefined) {
								trackEvent(productAnalyticsEvents.climatePro.dataGap.settings.interact, {
									deduplicationKey: `exclusion-select.${type}`,
								});
							}
						}}
					/>
				</FlexBlock>
				{!!formik.values.exclusions.filter(e => e.type === type).length && (
					<FlexBlock flexDirection="column" className={styles.excludedItemsWrapper}>
						<p>Excluded:</p>
						<FlexBlock columnGap="8px" rowGap="6px" flexWrap="wrap">
							{formik.values.exclusions
								.filter(e => e.type === type)
								.map((item, i) => (
									<FlexBlock
										key={i}
										columnGap="4px"
										alignItems="center"
										className={styles.excludedItem}
									>
										{
											entitiesData?.entities
												?.find(ent => ent.value === type)
												?.items.find(ent => ent.value === item.id)?.label
										}
										<button type="button" onClick={() => removeExclusion(type, item.id)}>
											<CloseIcon />
										</button>
									</FlexBlock>
								))}
						</FlexBlock>
					</FlexBlock>
				)}
			</div>
		);
	};

	return (
		<>
			{width < breakpoints.tabletWide && <SubmenuItems links={submenuLinks} />}
			<FormikProvider value={formik}>
				<Form onSubmit={formik.handleSubmit} className={styles.section}>
					<div>
						<h1>Settings</h1>
						<InfoStatus
							status="info"
							text="Travel arrangers receive action reminders for trips they booked. Company administrators receive an organization overview when completed trips still need details."
							className={styles.complianceExplanation}
						/>
						<Card>
							<h3>Automated reminder emails</h3>
							{isLoading && (
								<div className={styles.spinnerWrapper}>
									<Spinner absolute />
								</div>
							)}
							{isError && (
								<p className={styles.errorMessage} role="alert">
									Reminder settings could not be loaded. Please try again later.
								</p>
							)}
							{isSuccess && (
								<>
									<div className={styles.toggleSection}>
										<FlexBlock justifyContent="space-between" columnGap="20px">
											<p>Send monthly reminders</p>
											<FormikCheckboxToggle
												name="enabled"
												onValueChange={enabled =>
													trackEvent(
														productAnalyticsEvents.climatePro.dataGap.settings.interact,
														{
															deduplicationKey: 'reminders-toggle',
															postHogEventName: 'data_gap_reminder_toggle_clicked',
															postHogProperties: {
																category: 'data-gap',
																enabled,
															},
														},
													)
												}
											/>
										</FlexBlock>
										<p className={styles.info}>
											{formik.values.enabled
												? 'On the first of each month, travel arrangers receive action reminders for trips they booked, and company administrators receive an organization overview when trip details are missing.'
												: 'Turn on reminders to email travel arrangers and company administrators when trip details are missing.'}
										</p>
									</div>
									<div className={styles.exclusionsDisclosure}>
										<p>Should any people or organizational groups be excluded from these emails?</p>
										<button
											type="button"
											aria-expanded={isExclusionsOpen}
											onClick={() => {
												if (!isExclusionsOpen) {
													trackEvent(
														productAnalyticsEvents.climatePro.dataGap.settings.interact,
														{
															deduplicationKey: 'manage-exclusions',
														},
													);
												}
												setIsExclusionsOpen(prev => !prev);
											}}
										>
											{isExclusionsOpen ? 'Hide exclusions' : 'Manage exclusions'}
											<ArrowDownIcon className={classNames(isExclusionsOpen && styles.open)} />
										</button>
									</div>
									{isExclusionsOpen && (
										<div className={styles.exclusionsSection}>
											<h3>Reminder exclusions</h3>
											<p className={styles.info}>
												Excluded people and groups will not receive reminder or overview emails.
												Their trips will still appear in trip data coverage results.
											</p>
											{isEntitiesSuccess && renderSelectSection('legal_entity', 'Legal entities')}
											{isEntitiesSuccess && renderSelectSection('office', 'Offices')}
											{isEntitiesSuccess && renderSelectSection('department', 'Departments')}
											{isEntitiesSuccess && renderSelectSection('user', 'People')}
											{isEntitiesLoading && (
												<div className={styles.spinnerWrapper}>
													<Spinner absolute />
												</div>
											)}
											{isEntitiesError && (
												<p className={styles.errorMessage} role="alert">
													Reminder exclusions could not be loaded. Please try again later.
												</p>
											)}
										</div>
									)}
								</>
							)}
						</Card>
					</div>
				</Form>
			</FormikProvider>
			{hasUnsavedChanges && !isLoading && (
				<UnsavedChangesToast onCancel={() => formik.resetForm()} onSubmit={formik.handleSubmit} />
			)}
		</>
	);
};

export default Settings;
